import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { TabButton } from '../ui/tab-button'
import { useResponsive } from '../../hooks/useResponsive'
import { Icon } from '../ui/icon'

export type ViewerMode = 'View' | 'Annotate' | 'Shapes' | 'Insert' | 'Redact' | 'Research'

interface TabBarProps {
  activeMode: ViewerMode
  onModeChange: (mode: ViewerMode) => void
}

const MODES: { id: ViewerMode; nameKey: string; fallback: string }[] = [
  { id: 'View', nameKey: 'modes.View', fallback: 'View' },
  { id: 'Annotate', nameKey: 'modes.Annotate', fallback: 'Annotate' },
  { id: 'Shapes', nameKey: 'modes.Shapes', fallback: 'Shapes' },
  { id: 'Insert', nameKey: 'modes.Insert', fallback: 'Insert' },
  { id: 'Redact', nameKey: 'modes.Redact', fallback: 'Redact' },
  { id: 'Research', nameKey: 'modes.Research', fallback: 'Research' },
]

export const ViewerModeTabs = ({ activeMode, onModeChange }: TabBarProps) => {
  const { t } = useTranslation()
  const isCompact = useResponsive()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const activeLabel = t(MODES.find(m => m.id === activeMode)?.nameKey || '') || MODES.find(m => m.id === activeMode)?.fallback || 'View'

  if (isCompact) {
    return (
      <div className="flex items-center h-full">
        <div className="relative" ref={ref}>
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 h-12 px-4 text-sm font-medium text-accent hover:bg-interactive-hover rounded-lg transition-colors"
          >
            <span className="whitespace-nowrap">{activeLabel}</span>
            <Icon icon="chevronDown" size={14} className="text-fg-muted" />
          </button>
          {open && (
            <div className="fixed left-1/2 -translate-x-1/2 mt-1 bg-bg-surface border border-border-default rounded-xl shadow-2xl py-1 z-[9999] min-w-[140px]" style={{ top: '3.5rem' }}>
              {MODES.map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => { onModeChange(mode.id); setOpen(false) }}
                  className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                    activeMode === mode.id
                      ? 'text-accent font-medium bg-accent/5'
                      : 'text-fg-primary hover:bg-interactive-hover'
                  }`}
                >
                  {t(mode.nameKey) || mode.fallback}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center h-full">
      <div className="flex items-center gap-2">
        {MODES.map((mode) => (
          <TabButton
            key={mode.id}
            active={activeMode === mode.id}
            onClick={() => onModeChange(mode.id)}
            className="h-12 px-4"
          >
            <span className={`text-sm font-medium whitespace-nowrap ${activeMode === mode.id ? 'text-accent' : 'text-fg-secondary'}`}>
              {t(mode.nameKey) || mode.fallback}
            </span>
          </TabButton>
        ))}
      </div>
    </div>
  )
}
